import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Container from '../../common/components/Container/Container';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import { Routes } from '../../config/routes';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import Button, { ButtonStyle } from '../../common/components/Button/Button';
import ButtonContainer from '../../common/components/Button/ButtonContainer';
import { useRevocation } from '../../providers/RevocationContext';
import { Match } from '../../services/faceAuthentication/facetec/facetecProcessor';
import { addressFromDid, getChainAndIcon, truncate } from '../../utils/utils';

function RevocationItems(): JSX.Element {
  const { t } = useTranslation();
  const { revokedPasses, setSelectedPass } = useRevocation();
  const { flowSuccessful } = useContext(FlowConfigContext);
  const navigate = useNavigate();

  function handleCheckboxChange(item: Match) {
    setSelectedPass(item);
  }

  const handleDone = async () => {
    flowSuccessful();
  };

  const handleShowRevocations = async () => {
    navigate(Routes.REVOCATION_PASSES_LIST);
  };

  return (
    <>
      <div className="mb-2 flex justify-center w-full">
        <div className="w-1/2">
          <div className="text-primary font-medium">Address</div>
        </div>
        <div className="w-1/2">
          <div className="text-primary font-medium w-1/2">Network</div>
        </div>
      </div>

      <div className="flex flex-col justify-center w-full">
        {/* Subsequent rows */}
        {revokedPasses.map((item, index) => (
          <div
            key={index}
            className="text-textNeutral mb-2 flex"
            role="button"
            onClick={() => handleCheckboxChange(item)}
          >
            <div className="w-1/2">
              <span className="textDetail">{truncate(addressFromDid(item.did), 14)}</span>
            </div>
            <div className="w-1/2 flex">
              <div>
                <img className="w-6 mr-2" src={getChainAndIcon(item)?.icon} alt="" />
                <img className="w-6 mr-2" src={`/icons/chainIcons/${getChainAndIcon(item)?.icon}.svg`} alt="" />
              </div>
              <div>
                <span className="textDetail">{getChainAndIcon(item)?.chain}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ButtonContainer bottomMargin="mb-2 mt-8 mb-5 max-w-sm">
        <Button style={ButtonStyle.DEFAULT} disabled={false} dataTestid="CONFIRM_DONE_BUTTON" onClick={handleDone}>
          <span>{t('screens.revocationSuccess.doneButton')}</span>
        </Button>
        <Button
          style={ButtonStyle.INVERTED}
          disabled={false}
          dataTestid="CONFIRM_REVOCATION_BUTTON"
          onClick={handleShowRevocations}
        >
          <span>{t('screens.revocationSuccess.showRevocationsButton')}</span>
        </Button>
      </ButtonContainer>
    </>
  );
}

function RevocationConfirmScreen({ testId }: { testId: string }): JSX.Element {
  const { getExternalLinksConfig } = useContext(FlowConfigContext);
  const { civicSupportLink } = getExternalLinksConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const handleContactSupport = () => {
    navigate(Routes.SUPPORT, { state: { from: location.pathname } });
  };

  return (
    <Container>
      <TitleContainer center data-testid={testId}>
        <h1>{t('screens.revocationSuccess.title')}</h1>
        <div className="text-xl leading-6 my-4 text-textParagraph" data-testid="FACE_ISSUANCE_DESCRIPTION">
          <p>{t('screens.revocationSuccess.description')}</p>
        </div>
      </TitleContainer>
      <RevocationItems />
      <span className="w-full whitespace-pre-wrap textDetail text-center mt-6">
        {t('common.support.supportLinkQuestions')}
        <a target="_blank" title="Help Center" href={civicSupportLink} className="textLinkDetail" rel="noreferrer">
          {t('common.support.supportLinkVisitHelpCenter')}
        </a>
        {t('common.support.supportLinkOr')}
        <button
          type="button"
          className="buttonLink"
          title="Contact support"
          onClick={handleContactSupport}
          data-testid="CONTACT_SUPPORT_BUTTON"
        >
          {t('common.support.supportLinkContactSupport')}
        </button>
      </span>
    </Container>
  );
}

export default (): JSX.Element =>
  RevocationConfirmScreen({
    testId: 'REVOCATION_CONFIRM_SCREEN',
  });
